// Seeded randomness. The sim, the bots and the gates all draw from these so the same seed replays the same round,
// on the server, in the browser's offline driver and in the headless gates alike. (Math.random is never used in shared/.)

/** xorshift32 → [0, 1). Small, fast, and identical on every JS engine. */
export function makeRng(seed: number): () => number {
  let s = (seed >>> 0) || 0x9e3779b9;
  return () => { s ^= s << 13; s >>>= 0; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; };
}

export type Rng = () => number;
export const range = (r: Rng, lo: number, hi: number) => lo + (hi - lo) * r();
export const int = (r: Rng, lo: number, hiIncl: number) => lo + Math.floor(r() * (hiIncl - lo + 1));
export const pick = <T>(r: Rng, arr: readonly T[]): T => arr[Math.floor(r() * arr.length)];
/** a normal draw (Box-Muller), clamped to ±4 sd so a tail never produces an absurd reaction time */
export function normal(r: Rng, mean: number, sd: number): number {
  const u = Math.max(1e-9, r()), v = r();
  const z = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  return mean + sd * Math.max(-4, Math.min(4, z));
}
export function shuffle<T>(r: Rng, arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [arr[i], arr[j]] = [arr[j], arr[i]]; }
  return arr;
}
/** weighted index: weights need not sum to anything in particular */
export function weighted(r: Rng, w: readonly number[]): number {
  let sum = 0; for (const x of w) sum += Math.max(0, x);
  let t = r() * sum;
  for (let i = 0; i < w.length; i++) { t -= Math.max(0, w[i]); if (t < 0) return i; }
  return w.length - 1;
}
